import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
  ButtonNewRecord,
} from 'motion-components';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { CollectionOrderStatusBadge } from '../collection-order-status-badge';
import { formatDate } from 'shared-pages/utils/helpers';
import { promiseModalRoutes } from 'shared-pages/pages/collection-points-stats/grid/routes/RouteModal';
import { CollectionOrderStates, DummyCollectorId } from 'shared-pages/shared/constants';
import { FaArrowUp } from 'react-icons/fa';
import { isRebatteryLiion } from 'shared-pages/common/constants';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);
  const openRouteModal = async (collectionOrderId, collectorId) => {
    const result = await promiseModalRoutes({
      collectionOrderId,
      collectorId,
    });
  };
  const rowClicked = useCallback(
    (row) => {
      console.log('$$$', row.id);
      history.push({
        pathname: generatePath(getRouteByName('Order'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'code',
        header: t('Κωδικός Εντύπου'),
        columnName: t('Κωδικός Εντύπου'),
        accessorKey: 'code',
        orderByField: 'code',
        // cell: TableCellV8,
        cell: ({ getValue, row }) => (
          <div className="text-left">
            <div>
              {row.original.code}
              {row.original.isUrgent && (
                <>
                  &nbsp;
                  <FaArrowUp style={{ color: 'red', fontSize: 'smaller', marginBottom: '5px' }} />
                </>
              )}
            </div>
          </div>
        ),
        size: 20,
      },
      {
        id: 'orderedDate',
        header: t('Ημερομηνία Αιτήματος'),
        columnName: t('Ημερομηνία Αιτήματος'),
        accessorKey: 'orderedDate',
        orderByField: 'orderedDate',
        cell: CellDateV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'collectionOrderState.name',
        header: t('Κατάσταση Αιτήματος'),
        columnName: t('Κατάσταση Αιτήματος'),
        accessorKey: 'collectionOrderState.name',
        orderByField: 'collectionOrderState.name',
        cell: ({ row }) => {
          const data = row.original;
          return (
            <div className="d-flex align-items-center justify-content-center py-1 w-full">
              <CollectionOrderStatusBadge
                id={data?.statusId}
                value={data?.statusId}
                name={data?.collectionOrderState?.name}
              />
            </div>
          );
        },
        size: 10,
        meta: { type: 'text' },
      },
      ...(!isGridInForm
        ? [
            {
              id: 'collectionPointId',
              header: t('Σημείο Συλλογής'),
              columnName: t('Σημείο Συλλογής'),
              accessorKey: 'collectionpoint.name',
              orderByField: 'collectionpoint.name',
              cell: ({ getValue, row }) => (
                <div className="text-left">
                  <div>{row.original.collectionpoint.name}</div>
                  <div style={{ color: 'gray', fontSize: 'smaller' }}>
                    {row.original.collectionpoint.addressLine1},&nbsp;
                    {row.original.collectionpoint.addressArea},&nbsp;
                    {row.original.collectionpoint.addressCity},&nbsp;
                    {row.original.collectionpoint.addressDistrict}
                  </div>
                </div>
              ),
              size: 35,
              meta: { type: 'text' },
            },
          ]
        : []),
      {
        id: 'contactPerson',
        header: t('Πρόσωπο Επικοινωνίας & Τηλ. / Σημειώσεις'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'contactPerson',
        orderByField: 'contactPerson',
        cell: ({ getValue, row }) => (
          <div className="text-left">
            <div>
              {row.original.contactPerson ? `${row.original.contactPerson} - ` : ``}{' '}
              {row.original.collectionpoint.phone}
            </div>
            <div style={{ color: 'gray', fontSize: 'smaller' }}>{row.original.notes}</div>
          </div>
        ),

        size: 30,
        meta: { type: 'text' },
      },
      ...(!isRebatteryLiion
        ? [
            {
              id: 'collectionOrdersContainers',
              header: t('Συσκευασίες'),
              columnName: t('Συσκευασίες'),
              accessorKey: 'collectionOrdersContainers',
              orderByField: 'collectionOrdersContainers',
              cell: ({ getValue, row }) => {
                const containers = row.original.collectionOrdersContainers;
                if (!containers || containers.length === 0) {
                  return <div className="text-center">-</div>;
                }
                return (
                  <div className="text-left">
                    {containers.map((v, i) => {
                      if (v.containersQty)
                        return (
                          <div key={v.id || i}>
                            {v.containersQty} {v.containertype?.name}
                          </div>
                        );
                    })}
                  </div>
                );
              },
              size: 20,
            },
          ]
        : []),
      {
        id: 'collector.name',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collector.name',
        orderByField: 'collector.name',
        cell: ({ getValue, row }) => {
          // Dummy ή χωρίς συλλέκτη (π.χ. orphan FK) — εμφάνιση παύλας
          if (row.original.collectorId === DummyCollectorId || !row.original.collector) {
            return <div className="text-center">-</div>;
          }
          return (
            <div className="text-left">
              <div>{row.original.collector?.name}</div>
            </div>
          );
        },
        size: 20,
      },
      ...(!isRebatteryLiion &&
      (appStore.isPlatformOwner || appStore.isCollector || appStore.isCollectorDriver)
        ? [
            {
              id: 'userString1',
              header: t('Δρομολόγιο'),
              columnName: t('Δρομολόγιο'),
              accessorKey: 'userString1',
              orderByField: 'userString1',
              cell: ({ getValue, row }) => {
                const order = row.original;
                const statusId = order.statusId;
                if (statusId == CollectionOrderStates.Routed) {
                  const routeId =
                    order.routesLines && order.routesLines[0] ? order.routesLines[0].route.id : '';
                  const route =
                    order.routesLines && order.routesLines[0]
                      ? order.routesLines[0].route.code
                      : '';
                  const routeDate =
                    order.routesLines && order.routesLines[0]?.route
                      ? formatDate(order.routesLines[0]?.route?.routeDate)
                      : '';

                  return (
                    <div className="d-flex flex-column justify-content-center w-100 h-100 text-start">
                      <Link className="py-8" to={`route/${routeId}`}>
                        {route}
                      </Link>
                      {routeDate && <span>{routeDate}</span>}
                    </div>
                  );
                } else if (statusId == CollectionOrderStates.NotRouted) {
                  return (
                    <ButtonNewRecord
                      className={`align-self-center me-3 h-10 text-sm text-white `}
                      label="Σε Δρομολόγιο"
                      onClick={() => openRouteModal(row?.original?.id, row.original?.collectorId)}
                    />
                  );
                } else {
                  return <div className="text-center">-</div>;
                }
              },
              size: 20,
            },
          ]
        : []),
      {
        id: 'collectionId',
        header: t('Συλλογή'),
        columnName: t('Συλλογή'),
        accessorKey: 'collectionId',
        orderByField: 'collectionId',
        cell: ({ getValue, row }) => {
          const collectionId = row.original.collectionId ? row.original.collectionId : '';
          const collectionCode = row.original.collection ? row.original.collection.code : '';
          const dateCollected = row.original.collection
            ? formatDate(row.original.collection.dateCollected)
            : '-';
          if (!row.original.collectionId) {
            return <div className="text-center">-</div>;
          }
          // Δρομολόγηση Αιτήματος Συλλογής Μπαταριών Λιθίου
          if (
            isRebatteryLiion &&
            row.original.statusId === CollectionOrderStates.ScheduledCollectorCollection
          ) {
            return (
              <div className="text-center">
                <span className="text-muted">Προγραμματισμένη Συλλογή:</span>
                <br />
                {formatDate(row.original.plannedCollectionDate)}
              </div>
            );
          }
          // ---------------------------------------------
          return (
            <div className="d-flex flex-column justify-content-center w-100 h-100 text-start">
              {collectionCode && <Link to={`/collection/${collectionId}`}>{collectionCode}</Link>}
              <div style={{ color: 'gray', fontSize: 'smaller' }}>{dateCollected}</div>
            </div>
          );
        },
        size: 20,
      },
      ...(isRebatteryLiion
        ? [
            {
              id: 'idocs',
              header: t('Έντυπο Αναγνώρισης'),
              columnName: t('Έντυπο Αναγνώρισης'),
              accessorKey: 'idocs',
              orderByField: 'idocs',
              cell: ({ getValue, row }) => {
                const idocs = row.original.collection?.idocs;
                if (!idocs || idocs.length === 0) {
                  return <div className="text-center">-</div>;
                }
                return (
                  <div className="text-left">
                    {idocs.map((v) => (
                      <div key={v.id}>
                        <Link to={`/idoc/${v.id}`}>{v.identificationDocument}</Link>
                        <div style={{ color: 'gray', fontSize: 'smaller' }}>
                          {formatDate(v.dateCollected)}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              },
            },
          ]
        : []),
      // isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
      // ...(!isGridInForm && appStore.isPlatformOwner
      //   ? [
      //       {
      //         id: 'edit',
      //         header: t('Ενέργειες'),
      //         columnName: t('Ενέργειες'),
      //         accessorKey: 'edit',
      //         orderByField: 'lastUser',
      //         size: 120,
      //         cell: ({ row, table }) => {
      //           return true ? EditCellV8({ row, table }) : <>-</>;
      //         },
      //       },
      //     ]
      //   : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Containers - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addContainertype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Κάδο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteContainer({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Κάδος');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
